import type { FormEvent } from 'react';
import type React from 'react';

import {
  Flex,
  FormField,
  FormHelp,
  Grid,
  Heading,
  Input,
  Label,
  Paragraph,
  Spacer,
  Textarea,
  TextButton,
} from '@zillow/constellation';

import type Field from '../../../../models/Field';
import { PERSON } from '../../../../constants/index';
import { FULLSTORY_EXCLUDE } from '../../../../constants/analytics';
import type { Application } from '../../../../models/Types';

type Props = {
  index: number;
  onFieldChange: (event: FormEvent<HTMLElement>) => void;
  onRemovePerson: (event: FormEvent<HTMLElement>) => void;
  firstNameField: Field;
  firstNameValue: any;
  lastNameField: Field;
  lastNameValue: any;
  phoneNumberField: Field;
  phoneNumberValue: any;
  emailField: Field;
  emailValue: any;
  mailingAddressField?: Field;
  mailingAddressValue?: any;
  personTypeLabel: string;
  application?: Application;
  groupEndIndex?: number;
};

export default function PersonAndApplicantInformation({
  index,
  firstNameField,
  firstNameValue,
  lastNameField,
  lastNameValue,
  phoneNumberField,
  phoneNumberValue,
  emailField,
  emailValue,
  mailingAddressField,
  mailingAddressValue,
  onFieldChange,
  onRemovePerson,
  personTypeLabel,
  application,
  groupEndIndex,
}: Props): JSX.Element {
  function renderHeader() {
    return (
      <Flex display="flex" justifyContent="space-between" marginBottom="xs">
        <Paragraph fontType="bodyHeading">
          {groupEndIndex && groupEndIndex !== index + 1
            ? `Tenants #${index + 1} - ${groupEndIndex}`
            : `Tenant #${index + 1}`}
        </Paragraph>
        <TextButton data-test="remove-person" onClick={onRemovePerson}>
          Delete
        </TextButton>
      </Flex>
    );
  }

  function renderPerson(): React.ReactNode {
    const hasHeader = !application || index === 0 || index > application.applicants.length - 1;
    const isReadOnly = application && index <= application.applicants.length - 1;

    return (
      <>
        {hasHeader && renderHeader()}
        {!hasHeader && <Spacer marginTop="sm" />}
        {isReadOnly ? renderUneditableFields() : renderFields()}
      </>
    );
  }

  function renderUneditableFields(): React.ReactNode {
    return (
      <>
        <Heading level={5}>{`${firstNameValue} ${lastNameValue}`}</Heading>
        <Paragraph>{phoneNumberField ? `${emailValue} · ${phoneNumberValue}` : `${emailValue}`}</Paragraph>
        {mailingAddressField && (
          <FormField
            marginTop="sm"
            error={mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? true : false}
            label={<Label>Address for receiving notices prior to the lease’s start date</Label>}
            control={
              <Textarea
                data-field-name={mailingAddressField.fieldName}
                value={mailingAddressValue}
                onChange={onFieldChange}
                resize="vertical"
                rows="3"
                autofill="street-address"
              />
            }
            description={
              mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) && (
                <FormHelp>{mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}</FormHelp>
              )
            }
          />
        )}
      </>
    );
  }

  function renderFields(): React.ReactNode {
    return (
      <>
        <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" marginBottom="sm" gap="sm">
          <Grid gridColumnEnd={{ default: 'span 6', md_lte: 'span 12' }}>
            <FormField
              label={<Label>{PERSON.LABEL.FIRST_NAME}</Label>}
              control={
                <Input
                  data-field-name={firstNameField.fieldName}
                  type="fname"
                  value={firstNameValue}
                  onChange={onFieldChange}
                  autofill="given-name"
                />
              }
            />
          </Grid>
          <Grid gridColumnEnd={{ default: 'span 6', md_lte: 'span 12' }}>
            <FormField
              label={<Label id={`${personTypeLabel}#${index + 1}-last-name`}>{PERSON.LABEL.LAST_NAME}</Label>}
              control={
                <Input
                  aria-labelledby={`${personTypeLabel}#${index + 1}-last-name`}
                  data-field-name={lastNameField.fieldName}
                  type="lname"
                  value={lastNameValue}
                  onChange={onFieldChange}
                  autofill="family-name"
                />
              }
            />
          </Grid>
        </Grid>
        {mailingAddressField && (
          <FormField
            marginBottom="sm"
            error={mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) ? true : false}
            label={<Label>Address for receiving notices prior to the lease’s start date</Label>}
            control={
              <Textarea
                data-field-name={mailingAddressField.fieldName}
                value={mailingAddressValue}
                onChange={onFieldChange}
                resize="vertical"
                rows="3"
                autofill="street-address"
              />
            }
            description={
              mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS) && (
                <FormHelp>{mailingAddressField.showOnError(PERSON.ERROR_MESSAGE.MAILING_ADDRESS)}</FormHelp>
              )
            }
          />
        )}
        <FormField
          marginBottom={phoneNumberField ? 'sm' : 0}
          label={<Label>{PERSON.LABEL.EMAIL}</Label>}
          error={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            )
              ? true
              : false
          }
          control={
            <Input
              data-field-name={emailField.fieldName}
              value={emailValue}
              onChange={onFieldChange}
              // type='email' Disabled due to inconsistent/unpredicatable support across browsers
              autofill="email"
            />
          }
          description={
            emailField.showOnError(
              emailValue.includes(' ') ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
            ) && (
              <FormHelp>
                {emailField.showOnError(
                  emailValue.includes(' ')
                    ? PERSON.ERROR_MESSAGE.EMAIL_ADDRESS_SPACE
                    : PERSON.ERROR_MESSAGE.EMAIL_ADDRESS,
                )}
              </FormHelp>
            )
          }
        />
        {phoneNumberField && (
          <FormField
            error={phoneNumberField.showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER) ? true : false}
            label={<Label>{PERSON.LABEL.PHONE_NUMBER}</Label>}
            control={
              <Input
                data-field-name={phoneNumberField.fieldName}
                value={phoneNumberValue}
                onChange={onFieldChange}
                type="tel"
                autofill="tel"
              />
            }
            description={
              phoneNumberField.showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER) && (
                <FormHelp>{phoneNumberField.showOnError(PERSON.ERROR_MESSAGE.PHONE_NUMBER)}</FormHelp>
              )
            }
          />
        )}
      </>
    );
  }

  return (
    <div key={index} className={FULLSTORY_EXCLUDE}>
      {renderPerson()}
    </div>
  );
}
